import { useExternalRowSelectionApi } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi';

import {
  SIM_PRODUCT_ACTION,
  SimProductAction,
} from 'products/sim/Actions/constants/simProductActions.ts';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { rSimProductsQueries } from 'rSimInventory/utils/rsimProductsQueries.ts';
import { useRSimQueryData } from 'rSimInventory/Actions/hooks/useRSimQueryData.ts';
import { getAvailableActionsForRSims } from 'rSimInventory/Actions/api/rsimActionsApi.ts';

const ALL_IN_LIFE_ACTIONS_SET = new Set(Object.values(SIM_PRODUCT_ACTION));
const createInLifeActionsNotSupportedInitialSummary = () => {
  return Object.values(SIM_PRODUCT_ACTION).reduce(
    (inLifeActionsSummary, action) => ({
      ...inLifeActionsSummary,
      [action]: [],
    }),
    {} as Record<SimProductAction, string[]>,
  );
};

export const useEnabledRSimProductActions = () => {
  const rsimProducts = useRSimQueryData();

  const euiccs = rsimProducts?.items.map((rsim) => rsim.euicc) || [];
  const { data: availableRSimActions } = useQuery({
    queryKey: rSimProductsQueries.availableActions({ euiccs }),
    queryFn: () => getAvailableActionsForRSims({ euiccs }),
    enabled: false,
  });

  const { selectedIds } = useExternalRowSelectionApi();

  return useMemo(() => {
    const initial = {
      inLifeActions: new Set<SimProductAction>(),
      inLifeActionsNotSupportedSummary: createInLifeActionsNotSupportedInitialSummary(),
      inLifeActionsSupportedSummary: createInLifeActionsNotSupportedInitialSummary(),
    };
    if (!rsimProducts || !availableRSimActions) {
      return initial;
    }

    const selectedSimProduct = rsimProducts.items.filter(({ euicc }) => selectedIds.has(euicc));
    return selectedSimProduct.reduce(
      (
        { inLifeActions, inLifeActionsNotSupportedSummary, inLifeActionsSupportedSummary },
        { euicc },
      ) => {
        const availableActions = new Set(availableRSimActions?.[euicc] ?? []);
        const notSupportedInLifeActions = ALL_IN_LIFE_ACTIONS_SET.difference(availableActions);
        notSupportedInLifeActions.forEach((value) => {
          inLifeActionsNotSupportedSummary[value] = [
            ...inLifeActionsNotSupportedSummary[value],
            euicc,
          ];
        });

        availableActions.forEach((value) => {
          inLifeActionsSupportedSummary[value] = [...inLifeActionsSupportedSummary[value], euicc];
        });

        return {
          inLifeActions: new Set<SimProductAction>([...inLifeActions, ...availableActions]),
          inLifeActionsNotSupportedSummary: inLifeActionsNotSupportedSummary,
          inLifeActionsSupportedSummary: inLifeActionsSupportedSummary,
        };
      },
      initial,
    );
  }, [selectedIds, rsimProducts]);
};
